import { useNavigate, Link } from 'react-router-dom';
import { useTeamStore } from '@/store/useTeamStore';
import { routes } from '@/lib/routes';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { CharacterPortrait } from '@/tools/inner-team/components/team/CharacterPortrait';
import { MessageCircle, Users, ChevronRight } from 'lucide-react';

const concepts = [
  {
    emoji: '🎭',
    title: 'Viele Stimmen in dir',
    text: 'Jeder Mensch trägt ein inneres Team aus verschiedenen Anteilen in sich – der Kritiker, die Mutmacherin, der Zweifler, die Neugierige.',
  },
  {
    emoji: '💬',
    title: 'Im Gespräch entdecken',
    text: 'Unser KI-Assistent führt dich in einem offenen Gespräch durch deine innere Welt und hilft dir, deine Anteile zu benennen.',
  },
  {
    emoji: '🌱',
    title: 'Dein Team verstehen',
    text: 'Je besser du dein inneres Team kennst, desto bewusster kannst du Entscheidungen treffen und mit dir selbst in Kontakt sein.',
  },
];

export function Home() {
  const navigate = useNavigate();
  const members = useTeamStore((s) => s.members);

  return (
    <AppShell>
      <div className="flex-1 overflow-y-auto">
        {/* Hero */}
        <div className="px-6 pt-10 pb-8 text-center space-y-3">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Link to={routes.app.hub()} aria-label="Zur App-Übersicht" className="block rounded-lg hover:opacity-80 transition-opacity">
                <img src="/logo-wordmark.png" alt="Teamity" className="w-20 h-20 object-contain" />
              </Link>
              <span className="absolute -top-2 -right-12 bg-rose-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full rotate-12 shadow-md whitespace-nowrap tracking-wide pointer-events-none">
                Beta-Version
              </span>
            </div>
          </div>
          <p className="text-xs font-medium tracking-widest uppercase text-primary/70">
            Nach Schulz von Thun
          </p>
          <h1
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.02em' }}
            className="text-4xl font-medium leading-snug"
          >
            Lern dein<br />inneres Team kennen
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
            Entdecke die verschiedenen Stimmen und Persönlichkeitsanteile, die dich ausmachen.
          </p>
        </div>

        {/* Concept cards */}
        <div className="px-4 space-y-3 pb-6">
          {concepts.map((c) => (
            <div
              key={c.title}
              className="bg-card rounded-2xl px-5 py-4 flex gap-4 items-start border border-border"
            >
              <span className="text-2xl mt-0.5 shrink-0">{c.emoji}</span>
              <div className="space-y-1">
                <p className="text-sm font-medium">{c.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Team preview */}
        {members.length > 0 && (
          <div className="px-4 pb-6">
            <button
              onClick={() => navigate(routes.innerTeam.teamDashboard())}
              className="w-full bg-card rounded-2xl px-5 py-4 border border-border flex items-center gap-3 hover:bg-accent transition-colors"
            >
              <div className="flex -space-x-2 shrink-0">
                {members.slice(0, 4).map((m) => (
                  <div key={m.id} className="rounded-full border-2 border-card shrink-0 overflow-hidden">
                    <CharacterPortrait
                      emoji={m.avatarEmoji}
                      color={m.avatarColor}
                      avatarImageUrl={m.avatarImageUrl}
                      size="xs"
                    />
                  </div>
                ))}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium">Dein inneres Team</p>
                <p className="text-xs text-muted-foreground">
                  {members.length} {members.length === 1 ? 'Mitglied' : 'Mitglieder'} entdeckt
                </p>
              </div>
              <ChevronRight size={16} className="text-muted-foreground shrink-0" />
            </button>
          </div>
        )}

        {/* CTA */}
        <div className="px-4 pb-8 space-y-3">
          <Button
            size="lg"
            className="w-full gap-2"
            onClick={() => navigate(routes.innerTeam.discovery())}
          >
            <MessageCircle size={18} />
            {members.length === 0 ? 'Entdeckungsreise starten' : 'Weiter entdecken'}
          </Button>
          {members.length > 0 && (
            <Button
              variant="outline"
              size="lg"
              className="w-full gap-2"
              onClick={() => navigate(routes.innerTeam.teamDashboard())}
            >
              <Users size={18} />
              Mein Team ansehen
            </Button>
          )}
        </div>

        {/* Model credit */}
        <p className="text-center text-xs text-muted-foreground pb-8 px-6">
          Basierend auf dem Modell des{' '}
          <span className="italic">Inneren Teams</span>{' '}
          von Friedemann Schulz von Thun
        </p>
      </div>
    </AppShell>
  );
}
